import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import HideImageOutlinedIcon from "@mui/icons-material/HideImageOutlined";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import ENV from "../../../config/env";
import CentroEquipoDetalleDialog from "../components/CentroEquipoDetalleDialog";
import RequerimientoDetalle from "./RequerimientoDetalle";
import { getModemsEsperaPlataforma, procesarModemEsperaPlataforma } from "../services/reqSolicitudes";
import { getPlataformasActivas } from "../services/plataformas";
import ModemEsperaCard, { veredicto } from "../components/ModemEsperaCard";
import useConsultaPlataformas from "../hooks/useConsultaPlataformas";
import { claveReporte } from "../hooks/useVerificacionReporte";
import { CATEGORIAS_PENDIENTE, categoriaDe, modemAnteriorDe } from "../utils/categoriaPendiente";

/** Los módems de la página y, de cada reemplazo, también el anterior (para ver que se haya quitado de la plataforma). */
const conAnteriores = (rows) => [...rows, ...rows.map(modemAnteriorDe).filter(Boolean)];
const consultaAnteriorDe = (consultas, row) => {
  const anterior = modemAnteriorDe(row);
  return anterior ? consultas[claveReporte(anterior)] : undefined;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/La_Paz",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const get = (type) => parts.find((part) => part.type === type)?.value || "00";
  return `${get("year")}-${get("month")}-${get("day")} ${get("hour")}:${get("minute")}:${get("second")}`;
};

const valueOrDash = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
};

const toAbsUrl = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const base = ENV.API_BASE_URL || "";
  if (!base) return url;
  return `${base.replace(/\/$/, "")}/${String(url).replace(/^\//, "")}`;
};

const CLAVE_VISTA = "requerimientos_espera_plataforma:vista";
const leerVista = () => {
  try { return localStorage.getItem(CLAVE_VISTA) === "tabla" ? "tabla" : "tarjetas"; } catch { return "tarjetas"; }
};

const ThSort = ({ campo, label, orderBy, order, onSort }) => (
  <TableCell sx={{ fontWeight: 800 }} sortDirection={orderBy === campo ? order : false}>
    <TableSortLabel
      active={orderBy === campo}
      direction={orderBy === campo ? order : "asc"}
      onClick={() => onSort(campo)}
    >
      {label}
    </TableSortLabel>
  </TableCell>
);

export default function RequerimientosEsperaPlataformaPage() {
  const [detalleCod, setDetalleCod] = useState(null);
  // "tarjetas": cada módem con lo registrado y cómo está cargado/reportando en cada plataforma; "tabla": el listado compacto de siempre.
  const [vista, setVista] = useState(leerVista);
  const { consultas, consultarUno, consultarTodos } = useConsultaPlataformas();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(30);
  const [buscar, setBuscar] = useState("");
  const [buscarAplicado, setBuscarAplicado] = useState("");
  const [plataforma, setPlataforma] = useState("");
  const [plataformas, setPlataformas] = useState([]);
  // Filtro por categoría (adicion / mantenimiento / reemplazo / devolucion / otro); "" = todas.
  const [categoria, setCategoria] = useState("");
  const [conteos, setConteos] = useState({});
  const [orderBy, setOrderBy] = useState("fecha_proceso");
  const [order, setOrder] = useState("asc");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [equipoDialog, setEquipoDialog] = useState(null);
  const [imagenDialog, setImagenDialog] = useState(null);
  const [revisionRow, setRevisionRow] = useState(null);
  const [revisionForm, setRevisionForm] = useState({
    observacion: "",
    id_ref: "",
    grupo: "",
    sociedad: "",
    transportista: "",
  });
  const [savingRevision, setSavingRevision] = useState(false);
  const [revisionError, setRevisionError] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const list = await getPlataformasActivas();
        if (active) setPlataformas(Array.isArray(list) ? list : []);
      } catch {
        if (active) setPlataformas([]);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await getModemsEsperaPlataforma({
        buscar: buscarAplicado,
        plataforma,
        categoria: categoria || undefined,
        page: page + 1,
        limit,
        sortBy: orderBy,
        sortDir: order,
      });
      const payload = data?.data || data || {};
      setRows(Array.isArray(payload.items) ? payload.items : []);
      setTotal(Number(payload.total || 0));
      setConteos(payload.conteos || {});
    } catch (e) {
      setRows([]);
      setTotal(0);
      setError(e?.response?.data?.message || e?.message || "No se pudo cargar el listado.");
    } finally {
      setLoading(false);
    }
  }, [buscarAplicado, plataforma, categoria, page, limit, orderBy, order]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const cambiarVista = (_, v) => {
    if (!v) return;
    setVista(v);
    try { localStorage.setItem(CLAVE_VISTA, v); } catch { /* sin almacenamiento */ }
  };

  // En tarjetas se consultan las plataformas de los módems de la página actual (3 a la vez); al cambiar de página o de filtros se vuelve a consultar.
  useEffect(() => {
    if (vista === "tarjetas" && !loading && rows.length) consultarTodos(conAnteriores(rows));
  }, [vista, loading, rows, consultarTodos]);

  // Por el mismo veredicto de la tarjeta: en devolución "correcto" = retirado y "con error" = sigue cargado.
  const resumenConsultas = useMemo(() => {
    const tonos = rows.map((r) => veredicto(consultas[claveReporte(r)], r, consultaAnteriorDe(consultas, r)).tono);
    return {
      verificando: tonos.filter((t) => t === "consultando").length,
      correctos: tonos.filter((t) => t === "ok").length,
      conAviso: tonos.filter((t) => t === "aviso").length,
      conError: tonos.filter((t) => t === "error").length,
    };
  }, [rows, consultas]);

  const handleBuscar = () => {
    setPage(0);
    setBuscarAplicado(buscar.trim());
  };

  const handleSort = (campo) => {
    setOrder((prevOrder) => (orderBy === campo && prevOrder === "asc" ? "desc" : "asc"));
    setOrderBy(campo);
    setPage(0);
  };

  const openRevision = (row) => {
    setRevisionRow(row);
    setRevisionError("");
    setRevisionForm({
      observacion: "",
      id_ref: row?.id_ref || "",
      grupo: row?.grupo || "",
      sociedad: row?.sociedad || "",
      transportista: row?.transportista || "",
    });
  };

  const closeRevision = () => {
    if (savingRevision) return;
    setRevisionRow(null);
    setRevisionError("");
  };

  const handleRevisionChange = (event) => {
    const { name, value } = event.target;
    setRevisionForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveRevision = async () => {
    if (!revisionRow?.id_historial) return;
    setSavingRevision(true);
    setRevisionError("");
    try {
      await procesarModemEsperaPlataforma(revisionRow.id_historial, revisionForm);
      setRevisionRow(null);
      await fetchRows();
    } catch (e) {
      setRevisionError(e?.response?.data?.message || e?.message || "No se pudo guardar la revisión.");
    } finally {
      setSavingRevision(false);
    }
  };

  const platformName = useMemo(() => {
    if (!plataforma) return "Todas";
    return plataformas.find((p) => String(p.id) === String(plataforma))?.nombre || plataforma;
  }, [plataforma, plataformas]);

  return (
    <DashboardLayout>
      <PageHeader
        title="Requerimientos en espera de plataforma"
        subtitle="Módems pendientes para revisar asignación y reporte en plataforma"
        backTo="/requerimiento"
      />

      <Box sx={{ p: { xs: 1, md: 1.25 } }}>
        <Paper variant="outlined" sx={{ p: 1, mb: 1, borderRadius: 1 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={0.75} alignItems={{ xs: "stretch", md: "center" }}>
            <TextField
              size="small"
              label="Buscar"
              value={buscar}
              onChange={(e) => setBuscar(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleBuscar();
              }}
              placeholder="Req, cliente, SIM, EMAI, placa, grupo..."
              sx={{ minWidth: { md: 340 }, "& .MuiInputBase-input": { fontSize: "0.78rem", py: 0.7 } }}
            />
            <FormControl size="small" sx={{ minWidth: { md: 220 } }}>
              <InputLabel>Plataforma</InputLabel>
              <Select
                label="Plataforma"
                value={plataforma}
                onChange={(e) => {
                  setPlataforma(e.target.value);
                  setPage(0);
                }}
                sx={{ "& .MuiSelect-select": { fontSize: "0.78rem", py: 0.7 } }}
              >
                <MenuItem value="">Todas</MenuItem>
                {plataformas.map((p) => (
                  <MenuItem key={p.id} value={String(p.id)}>
                    {p.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              size="small"
              startIcon={<SearchIcon />}
              onClick={handleBuscar}
              sx={{ minHeight: 34, bgcolor: "#006d77", "&:hover": { bgcolor: "#00565f" } }}
            >
              Buscar
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={fetchRows}
              sx={{ minHeight: 34 }}
            >
              Actualizar
            </Button>
            <Box sx={{ flex: 1 }} />
            <ToggleButtonGroup exclusive size="small" color="primary" value={vista} onChange={cambiarVista} sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontSize: 12, py: 0.25, px: 1.25, gap: 0.5 } }}>
              <ToggleButton value="tarjetas"><ViewModuleIcon sx={{ fontSize: 16 }} />Tarjetas</ToggleButton>
              <ToggleButton value="tabla"><ViewListIcon sx={{ fontSize: 16 }} />Tabla</ToggleButton>
            </ToggleButtonGroup>
            <Chip size="small" label={`Total: ${total}`} />
            <Chip size="small" variant="outlined" label={`Plataforma: ${platformName}`} />
          </Stack>
          {/* Filtro por categoría; las vacías no se muestran (salvo la elegida). */}
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            <Chip
              size="small"
              label={`Todos · ${Object.values(conteos).reduce((a, n) => a + Number(n || 0), 0)}`}
              color={categoria ? "default" : "primary"}
              variant={categoria ? "outlined" : "filled"}
              onClick={() => { setCategoria(""); setPage(0); }}
            />
            {CATEGORIAS_PENDIENTE.filter((c) => Number(conteos[c.clave] || 0) > 0 || c.clave === categoria).map((c) => {
              const elegida = c.clave === categoria;
              const { Icono } = c;
              return (
                <Chip
                  key={c.clave}
                  size="small"
                  icon={<Icono sx={{ fontSize: "16px !important", color: `${elegida ? "#fff" : c.color} !important` }} />}
                  label={`${c.etiqueta} · ${Number(conteos[c.clave] || 0)}`}
                  variant={elegida ? "filled" : "outlined"}
                  onClick={() => { setCategoria(elegida ? "" : c.clave); setPage(0); }}
                  sx={elegida
                    ? { bgcolor: c.color, color: "#fff", fontWeight: 700, "&:hover": { bgcolor: c.color } }
                    : { borderColor: c.color, color: c.color, fontWeight: 600 }}
                />
              );
            })}
          </Stack>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        <Paper
          variant="outlined"
          sx={vista === "tarjetas"
            ? { border: 0, bgcolor: "transparent", backgroundImage: "none", overflow: "visible" } // las tarjetas van directo sobre el fondo (sin tarjeta contenedora)
            : { borderRadius: 1, overflow: "hidden" }}
        >
          {loading && <CircularProgress size={18} sx={{ m: 1 }} />}
          {vista === "tarjetas" ? (
            <Box sx={{ display: "grid", gap: 1 }}>
              {!loading && (
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip size="small" color="success" variant="outlined" label={`Correctos: ${resumenConsultas.correctos}`} />
                  <Chip size="small" color="warning" variant="outlined" label={`Con aviso: ${resumenConsultas.conAviso}`} />
                  <Chip size="small" color="error" variant="outlined" label={`Con error: ${resumenConsultas.conError}`} />
                  {resumenConsultas.verificando > 0 && <Chip size="small" icon={<CircularProgress size={12} />} label={`Consultando ${resumenConsultas.verificando}…`} />}
                  <Box sx={{ flex: 1 }} />
                  <Button size="small" startIcon={<RefreshIcon />} disabled={resumenConsultas.verificando > 0 || !rows.length} onClick={() => consultarTodos(conAnteriores(rows))} sx={{ textTransform: "none" }}>
                    Consultar plataformas de nuevo
                  </Button>
                </Stack>
              )}
              {!loading && rows.length === 0 ? (
                <Typography sx={{ color: "text.secondary", fontStyle: "italic", p: 1 }}>No hay módems en espera.</Typography>
              ) : (
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" }, gap: 1.25 }}>
                  {rows.map((row) => (
                    <ModemEsperaCard
                      key={row.id_historial}
                      row={row}
                      consulta={consultas[claveReporte(row)]}
                      consultaAnterior={consultaAnteriorDe(consultas, row)}
                      onConsultar={() => {
                        consultarUno(row);
                        const anterior = modemAnteriorDe(row);
                        if (anterior) consultarUno(anterior);
                      }}
                      onVerReq={() => setDetalleCod(row.cod)}
                      onVerEquipo={() => setEquipoDialog({ tipo: "modem", identificador: row.num_sim || row.emai })}
                      onVerEquipoAnterior={() => {
                        const anterior = modemAnteriorDe(row);
                        if (anterior) setEquipoDialog({ tipo: "modem", identificador: anterior.num_sim || anterior.emai });
                      }}
                      onVerImagen={() => setImagenDialog(row.imagen_url)}
                      onRevisar={() => openRevision(row)}
                      formatDateTime={formatDateTime}
                      toAbsUrl={toAbsUrl}
                    />
                  ))}
                </Box>
              )}
            </Box>
          ) : (
            <TableContainer>
              <Table size="small" sx={{ "& th, & td": { px: 0.75, py: 0.55, fontSize: "0.74rem" } }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f1f5f9" }}>
                    <ThSort campo="cod" label="Req" orderBy={orderBy} order={order} onSort={handleSort} />
                    <ThSort campo="fecha" label="Fecha" orderBy={orderBy} order={order} onSort={handleSort} />
                    <ThSort campo="fecha_proceso" label="Fecha proceso" orderBy={orderBy} order={order} onSort={handleSort} />
                    <ThSort campo="tipo_req" label="Tipo" orderBy={orderBy} order={order} onSort={handleSort} />
                    <ThSort campo="razon_social" label="Cliente / Servicio" orderBy={orderBy} order={order} onSort={handleSort} />
                    <ThSort campo="plataforma" label="Plataforma" orderBy={orderBy} order={order} onSort={handleSort} />
                    <ThSort campo="num_sim" label="SIM / EMAI" orderBy={orderBy} order={order} onSort={handleSort} />
                    <TableCell sx={{ fontWeight: 800 }} align="center">Imagen</TableCell>
                    <ThSort campo="placa" label="Placa" orderBy={orderBy} order={order} onSort={handleSort} />
                    <ThSort campo="id_ref" label="Alias" orderBy={orderBy} order={order} onSort={handleSort} />
                    <ThSort campo="grupo" label="Grupo" orderBy={orderBy} order={order} onSort={handleSort} />
                    <ThSort campo="sociedad" label="Sociedad" orderBy={orderBy} order={order} onSort={handleSort} />
                    <ThSort campo="transportista" label="Transportista" orderBy={orderBy} order={order} onSort={handleSort} />
                    <TableCell sx={{ fontWeight: 800 }}>Obs.</TableCell>
                    <TableCell sx={{ fontWeight: 800, textAlign: "right" }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!loading && rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={15} sx={{ color: "text.secondary", fontStyle: "italic" }}>
                        No hay módems en espera.
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow key={row.id_historial} hover>
                        <TableCell>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => setDetalleCod(row.cod)}
                            sx={{ minWidth: 0, px: 0.25, fontSize: "0.74rem", fontWeight: 800 }}
                          >
                            #{row.cod}
                          </Button>
                        </TableCell>
                        <TableCell>{formatDateTime(row.fecha)}</TableCell>
                        <TableCell>{formatDateTime(row.fecha_proceso)}</TableCell>
                        <TableCell>
                          {(() => {
                            const c = categoriaDe(row);
                            return (
                              <Chip
                                size="small"
                                label={c.etiqueta}
                                title={row.tipo_req || ""}
                                sx={{ height: 20, fontSize: "0.68rem", fontWeight: 700, bgcolor: c.color, color: "#fff" }}
                              />
                            );
                          })()}
                        </TableCell>
                        <TableCell sx={{ minWidth: 220 }}>
                          <Typography sx={{ fontSize: "0.74rem", fontWeight: 700 }}>
                            {valueOrDash(row.razon_social)}
                          </Typography>
                          <Typography sx={{ fontSize: "0.68rem", color: "text.secondary" }}>
                            {row.id_serv} - {valueOrDash(row.descripcion)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.plataforma || row.id_plataforma || "-"}
                            sx={{ height: 20, fontSize: "0.68rem" }}
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 150 }}>
                          <Box
                            role="button"
                            tabIndex={0}
                            onClick={() => setEquipoDialog({ tipo: "modem", identificador: row.num_sim || row.emai })}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") setEquipoDialog({ tipo: "modem", identificador: row.num_sim || row.emai });
                            }}
                            sx={{ cursor: "pointer", display: "inline-block" }}
                          >
                            <Typography sx={{ fontSize: "0.74rem", fontWeight: 800, color: "#006d77", textDecoration: "underline" }}>
                              {valueOrDash(row.num_sim)}
                            </Typography>
                            <Typography sx={{ fontSize: "0.68rem", color: "text.secondary" }}>{valueOrDash(row.emai)}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          {row.imagen_url ? (
                            <Tooltip title="Ver imagen">
                              <Box
                                role="button"
                                tabIndex={0}
                                onClick={() => setImagenDialog(row.imagen_url)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") setImagenDialog(row.imagen_url);
                                }}
                                sx={{ display: "inline-block", lineHeight: 0, cursor: "pointer" }}
                              >
                                <Box
                                  component="img"
                                  src={toAbsUrl(row.imagen_url)}
                                  alt="Evidencia"
                                  sx={{
                                    width: 34,
                                    height: 34,
                                    objectFit: "cover",
                                    borderRadius: 1,
                                    border: "1px solid #e2e8f0",
                                    display: "block",
                                  }}
                                />
                              </Box>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Sin imagen">
                              <HideImageOutlinedIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>{valueOrDash(row.placa)}</TableCell>
                        <TableCell>{valueOrDash(row.id_ref)}</TableCell>
                        <TableCell>{valueOrDash(row.grupo)}</TableCell>
                        <TableCell>{valueOrDash(row.sociedad)}</TableCell>
                        <TableCell>{valueOrDash(row.transportista)}</TableCell>
                        <TableCell sx={{ maxWidth: 240 }}>
                          <Tooltip title={row.observacion || row.obs_req || ""}>
                            <Typography noWrap sx={{ fontSize: "0.74rem" }}>
                              {valueOrDash(row.observacion || row.obs_req)}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            {row.url_plataforma && (
                              <Tooltip title="Abrir plataforma">
                                <Button
                                  size="small"
                                  variant="text"
                                  startIcon={<OpenInNewIcon fontSize="small" />}
                                  onClick={() => {
                                    const url = /^https?:\/\//i.test(row.url_plataforma)
                                      ? row.url_plataforma
                                      : `https://${row.url_plataforma}`;
                                    window.open(url, "_blank", "noopener,noreferrer");
                                  }}
                                  sx={{ minWidth: 0, px: 0.5 }}
                                >
                                  Web
                                </Button>
                              </Tooltip>
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon fontSize="small" />}
                              onClick={() => openRevision(row)}
                              sx={{ minWidth: 0, px: 0.75 }}
                            >
                              Revisar
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={limit}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[15, 30, 50, 100]}
            labelRowsPerPage="Límite"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={vista === "tarjetas" ? { mt: 1, bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 1 } : undefined}
          />
        </Paper>
      </Box>

      <Dialog open={!!imagenDialog} onClose={() => setImagenDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>Imagen / Evidencia</DialogTitle>
        <DialogContent dividers sx={{ p: 1, textAlign: "center" }}>
          {imagenDialog && (
            <Box
              component="img"
              src={toAbsUrl(imagenDialog)}
              alt="Evidencia"
              sx={{
                display: "block",
                width: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
                borderRadius: 1,
                border: "1px solid #e2e8f0",
                bgcolor: "#fff",
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 1 }}>
          <Button size="small" href={toAbsUrl(imagenDialog)} target="_blank" rel="noreferrer">
            Abrir en pestaña nueva
          </Button>
          <Button size="small" variant="contained" onClick={() => setImagenDialog(null)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {detalleCod && (
        <Dialog
          open={!!detalleCod}
          onClose={() => setDetalleCod(null)}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 1.25, maxHeight: "92vh", bgcolor: "background.paper", color: "text.primary" },
          }}
        >
          <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>
            Requerimiento #{detalleCod}
          </DialogTitle>
          <DialogContent dividers sx={{ p: 1, bgcolor: "background.default", color: "text.primary" }}>
            <RequerimientoDetalle codProp={detalleCod} embedded onBack={() => setDetalleCod(null)} />
          </DialogContent>
          <DialogActions sx={{ p: 0.75 }}>
            <Button size="small" onClick={() => setDetalleCod(null)}>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <CentroEquipoDetalleDialog
        open={!!equipoDialog}
        tipo={equipoDialog?.tipo || "modem"}
        identificador={equipoDialog?.identificador}
        onClose={() => setEquipoDialog(null)}
      />

      <Dialog open={!!revisionRow} onClose={closeRevision} fullWidth maxWidth="sm">
        <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>
          Revisar módem en espera
          <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
            Req #{revisionRow?.cod || "-"} - {revisionRow?.num_sim || "-"}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1 }}>
          <Stack spacing={1}>
            {revisionError && <Alert severity="error">{revisionError}</Alert>}
            {revisionRow?.imagen_url && (
              <Paper variant="outlined" sx={{ p: 0.75, borderRadius: 1, bgcolor: "#f8fafc" }}>
                <Stack spacing={0.75}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Typography sx={{ fontSize: "0.75rem", fontWeight: 800, color: "text.secondary" }}>
                      Imagen cargada
                    </Typography>
                    <Button
                      size="small"
                      variant="text"
                      href={toAbsUrl(revisionRow.imagen_url)}
                      target="_blank"
                      rel="noreferrer"
                      sx={{ minHeight: 24, py: 0, fontSize: "0.72rem" }}
                    >
                      Abrir imagen
                    </Button>
                  </Stack>
                  <Box
                    component="a"
                    href={toAbsUrl(revisionRow.imagen_url)}
                    target="_blank"
                    rel="noreferrer"
                    sx={{ display: "block" }}
                  >
                    <Box
                      component="img"
                      src={toAbsUrl(revisionRow.imagen_url)}
                      alt="Evidencia del modem"
                      sx={{
                        display: "block",
                        width: "100%",
                        maxHeight: 260,
                        objectFit: "contain",
                        borderRadius: 1,
                        border: "1px solid #e2e8f0",
                        bgcolor: "#fff",
                      }}
                    />
                  </Box>
                </Stack>
              </Paper>
            )}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1 }}>
              <TextField size="small" label="Alias / ID Ref" name="id_ref" value={revisionForm.id_ref} onChange={handleRevisionChange} />
              <TextField size="small" label="Grupo" name="grupo" value={revisionForm.grupo} onChange={handleRevisionChange} />
              <TextField size="small" label="Sociedad" name="sociedad" value={revisionForm.sociedad} onChange={handleRevisionChange} />
              <TextField size="small" label="Transportista" name="transportista" value={revisionForm.transportista} onChange={handleRevisionChange} />
            </Box>
            <TextField
              size="small"
              label="Observación"
              name="observacion"
              value={revisionForm.observacion}
              onChange={handleRevisionChange}
              multiline
              minRows={3}
              fullWidth
            />
            <Alert severity="info" sx={{ py: 0 }}>
              Se marcará como procesado según el tipo de requerimiento: Activo, Inactivo o Vendido.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 1 }}>
          <Button onClick={closeRevision} disabled={savingRevision}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={saveRevision}
            disabled={savingRevision}
            sx={{ bgcolor: "#006d77", "&:hover": { bgcolor: "#00565f" } }}
          >
            {savingRevision ? "Guardando..." : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
